import React, { useState, useEffect } from "react";
import AdminPanel from "../panel/AdminPanel";
import api from "../../../api/api";
import { FaCheck, FaSearch } from "react-icons/fa";
import "../styles/AssignStudents.css";



const AssignStudents = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [searchStudent, setSearchStudent] = useState("");
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [rollNumber, setRollNumber] = useState(""); // for single student
  const [rollNumbers, setRollNumbers] = useState({}); // for multiple students

  useEffect(() => {
    fetchClasses();
    fetchStudents();
    fetchAssignedStudents();
  }, []);

  const fetchAssignedStudents = async () => {
    try {
      const res = await api.get(`/api/assign/assigned-students`)
      const assignedIds = res.data.map((s) => Number(s.student_id));
      setAssignedStudents(assignedIds);
    } catch (err) {
      console.error("Error fetching assigned students:", err);
    }
  };

  const fetchClasses = async () => {
    try {
      const res = await api.get(`/api/classes`);
      setClasses(res.data);
    } catch (err) {
      console.error("Error fetching classes:", err);
    }
  };

  const fetchSections = async (classId) => {
    if (!classId) {
      setSections([]);
      return;
    }
    try {
      const res = await api.get(`/api/sections?classId=${classId}`)
      setSections(res.data);
    } catch (err) {
      console.error("Error fetching sections:", err);
    }
  };

  const fetchStudents = async () => {
    try {
      const res = await api.get(`/api/students`);
      const studentsFromDb = res.data.map((s, index) => ({
        ...s,
        id: s.student_id ? Number(s.student_id) : index + 1,
      }));
      setStudents(studentsFromDb);
    } catch (err) {
      console.error("Error fetching students:", err);
    }
  };

  const toggleStudentSelection = (studentId) => {
    if (assignedStudents.includes(studentId)) return;
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleAssign = async (e) => {
    e.preventDefault();

    if (selectedStudents.length === 0) {
      return alert("Please select at least one student.");
    }

    // Check for missing roll numbers
    if (selectedStudents.length > 1) {
      for (let id of selectedStudents) {
        if (!rollNumbers[id]) {
          return alert("Please enter roll number for all selected students.");
        }
      }
    } else if (selectedStudents.length === 1 && !rollNumber) {
      return alert("Please enter roll number for the selected student.");
    }

    const studentsToAssign = selectedStudents.map((id) => {
      const student = students.find((s) => s.id === id);
      return {
        id: student.id,
        name: student.name,
        roll_number:
          selectedStudents.length === 1
            ? rollNumber
            : rollNumbers[id] || null,
        section: selectedSection,
      };
    });

    try {
      await api.post(`/api/assign/assign-students`, {
        classId: Number(selectedClass),
        students: studentsToAssign,
      });
      alert("✅ Students successfully assigned!");
      setSelectedStudents([]);
      setRollNumber("");
      setRollNumbers({});
      setSelectedSection("");
      fetchAssignedStudents();
    } catch (err) {
      console.error("Error assigning students:", err);
      alert("❌ Something went wrong.");
    }
  };

  const filteredStudents = students.filter(
    (s) =>
      s.name.toLowerCase().includes(searchStudent.toLowerCase()) ||
      s.email.toLowerCase().includes(searchStudent.toLowerCase())
  );

  return (
    <>
      <AdminPanel onToggle={setIsSidebarCollapsed} />
      <div className={`sr-main-content5 ${isSidebarCollapsed ? "collapsed" : ""}`}>
        <h2>Assign Students to Classes</h2>
        <p>Select students and assign them to a class.</p>

        <form onSubmit={handleAssign}>
          <div className="selection-search-wrapper">
            {/* Class */}
            <div className="class-selection">
              <label>Select Class:</label>
              <select
                value={selectedClass}
                onChange={(e) => {
                  const classId = e.target.value;
                  setSelectedClass(classId);
                  setSelectedSection(""); // reset section
                  fetchSections(classId);
                }}
                required
              >
                <option value="">-- Select Class --</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Roll Number (top) + Section */}
            <div className="roll-section-inputs">
              {selectedStudents.length === 1 && (
                <>
                  <label>Roll Number:</label>
                  <input
                    type="text"
                    placeholder="Enter Roll Number"
                    value={rollNumber}
                    onChange={(e) => setRollNumber(e.target.value)}
                    required
                  />
                </>
              )}

              <label>Section:</label>
              <select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                required
              >
                <option value="">-- Select Section --</option>
                {sections.map((sec) => (
                  <option key={sec.id} value={sec.name}>{sec.name}</option>
                ))}
              </select>
            </div>

            {/* Search Students */}
            <div className="student-search">
              <FaSearch />
              <input
                type="text"
                placeholder="Search Students..."
                value={searchStudent}
                onChange={(e) => setSearchStudent(e.target.value)}
              />
            </div>
          </div>

          {/* Students Table */}
          <div className="student-table">
            <table>
              <thead>
                <tr>
                  <th>Select</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Blood Group</th>
                  <th>Address</th>
                  {selectedStudents.length > 1 && <th>Roll #</th>}
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((s) => {
                  const isAssigned = assignedStudents.includes(s.id);
                  return (
                    <tr key={s.id} className={isAssigned ? "disabled" : ""}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(s.id)}
                          onChange={() => toggleStudentSelection(s.id)}
                          disabled={isAssigned}
                        />
                      </td>
                      <td>{s.name}</td>
                      <td>{s.email}</td>
                      <td>{s.bloodGroup}</td>
                      <td>{s.address}</td>
                      {/* Roll number input for multiple selections */}
                      {selectedStudents.length > 1 && selectedStudents.includes(s.id) && (
                        <td>
                          <input
                            type="text"
                            placeholder="Roll #"
                            value={rollNumbers[s.id] || ""}
                            onChange={(e) =>
                              setRollNumbers((prev) => ({ ...prev, [s.id]: e.target.value }))
                            }
                            required
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Assign Button */}
          <button type="submit" className="assign-btn">
            <FaCheck style={{ marginRight: "5px" }} /> Assign Selected Students
          </button>
        </form>
      </div>
    </>
  );
};

export default AssignStudents;
